import { addDays, differenceInCalendarDays, format } from "date-fns";

export type SpacedScheduleItem = {
  scheduledDate: string;
  orderIndex: number;
};

const parseDateKey = (dateKey: string) => new Date(`${dateKey}T00:00:00`);

const toDateKey = (date: Date) => format(date, "yyyy-MM-dd");

/**
 * Place copied workouts on new dates while keeping the gaps between them.
 * The earliest workout lands on `targetDate`; the rest keep their original offsets.
 */
export function copyPlacementsPreservingSpacing<T extends SpacedScheduleItem>(
  items: T[],
  targetDate: string,
): T[] {
  if (items.length === 0) return [];

  const sorted = [...items].sort((a, b) => {
    if (a.scheduledDate !== b.scheduledDate) {
      return a.scheduledDate.localeCompare(b.scheduledDate);
    }
    return a.orderIndex - b.orderIndex;
  });
  const anchor = parseDateKey(sorted[0].scheduledDate);
  const target = parseDateKey(targetDate);

  return sorted.map((item) => ({
    ...item,
    scheduledDate: toDateKey(
      addDays(
        target,
        differenceInCalendarDays(parseDateKey(item.scheduledDate), anchor),
      ),
    ),
  }));
}

type RelocatableScheduleItem = SpacedScheduleItem & { id: string };

const insertAt = <T,>(existing: T[], incoming: T[], index: number): T[] => {
  const clamped = Math.max(0, Math.min(index, existing.length));
  return [
    ...existing.slice(0, clamped),
    ...incoming,
    ...existing.slice(clamped),
  ];
};

/**
 * Move a selected group so the dragged workout lands on `targetDate`.
 * Every other selected workout keeps its original gap from that workout.
 * Returns null when the drop would not change a multi-day group.
 */
export function relocateScheduleGroup<T extends RelocatableScheduleItem>(
  items: T[],
  selectedIds: readonly string[],
  anchorId: string,
  targetDate: string,
  insertIndex: number,
): T[] | null {
  const selectedIdSet = new Set(selectedIds);
  if (!selectedIdSet.has(anchorId) || selectedIdSet.size < 2) return null;

  const selected = items.filter((item) => selectedIdSet.has(item.id));
  if (selected.length < 2) return null;
  const anchor = selected.find((item) => item.id === anchorId);
  if (!anchor) return null;

  const delta = differenceInCalendarDays(
    parseDateKey(targetDate),
    parseDateKey(anchor.scheduledDate),
  );
  const spansMultipleDays = selected.some(
    (item) => item.scheduledDate !== anchor.scheduledDate,
  );
  if (delta === 0 && spansMultipleDays) return null;

  const shifted = selected.map((item) => ({
    ...item,
    scheduledDate: toDateKey(addDays(parseDateKey(item.scheduledDate), delta)),
  }));
  const staying = items.filter((item) => !selectedIdSet.has(item.id));
  const dates = [
    ...new Set([
      ...items.map((item) => item.scheduledDate),
      ...shifted.map((item) => item.scheduledDate),
    ]),
  ].sort();

  const next: T[] = [];
  dates.forEach((date) => {
    const existing = staying
      .filter((item) => item.scheduledDate === date)
      .sort((a, b) => a.orderIndex - b.orderIndex);
    const incoming = shifted
      .filter((item) => item.scheduledDate === date)
      .sort((a, b) => a.orderIndex - b.orderIndex);
    const merged =
      date === targetDate
        ? insertAt(existing, incoming, insertIndex)
        : [...existing, ...incoming];
    merged.forEach((item, index) => {
      next.push({ ...item, scheduledDate: date, orderIndex: index });
    });
  });
  return next;
}
